/** HTTP contract shared with the API (backend `app/core/constants/auth.py`, DEC-020). */
export const API_HEADERS = {
  csrf: "X-CSRF-Token",
  workspace: "X-Workspace-Id",
  requestId: "X-Request-ID",
  authorization: "Authorization",
} as const;

/** Readable double-submit CSRF cookie (the refresh cookie itself is HttpOnly). */
export const CSRF_COOKIE_NAME = "sagot_csrf";

export type AuthRealm = "console" | "admin";

export const AUTH_ENDPOINTS = {
  console: { refresh: "/v1/auth/refresh", logout: "/v1/auth/logout" },
  admin: { refresh: "/admin/v1/auth/refresh", logout: "/admin/v1/auth/logout" },
} as const satisfies Record<AuthRealm, { refresh: string; logout: string }>;

/** The sign-in flow itself: sent without a bearer token and never refreshed-and-retried. */
export const AUTH_PATH_PREFIXES = ["/v1/auth/", "/admin/v1/auth/"] as const;

/** Cross-tab coordination names (Web Locks + BroadcastChannel). */
export const SESSION_SYNC = {
  lockPrefix: "sagot-refresh",
  channelPrefix: "sagot-session",
} as const;

/** Refresh this long before the access token expires (DEC-006 tokens live 15 min). */
export const ACCESS_TOKEN_REFRESH_SKEW_MS = 30_000;
export const DEFAULT_REQUEST_TIMEOUT_MS = 30_000;

/** Default API base: same-origin `/api` (Vite proxy in dev, rewrite in prod) unless overridden. */
export const DEFAULT_API_BASE_URL = "/api";

/** Admin → console hand-off: `#impersonation=<token>` (fragment, never sent to servers or logs). */
export const IMPERSONATION_HASH_PARAM = "impersonation";
/** Console → admin: `/tenants?end_impersonation=<id>` ends the support session with staff credentials. */
export const END_IMPERSONATION_QUERY_PARAM = "end_impersonation";

/** Cloudflare's always-pass test site key (local development only). */
export const TURNSTILE_TEST_SITE_KEY = "1x00000000000000000000AA";
export const TURNSTILE_SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

/** Code lengths and timers (scope M1, screens §1.3/§1.4). */
export const OTP_LENGTH = 6;
export const RESEND_COOLDOWN_SECONDS = 30;
export const BACKUP_CODE_PATTERN = /^[A-Z0-9]{4}-?[A-Z0-9]{4}$/;
export const PASSWORD_MIN_LENGTH = 10;
export const PASSWORD_MAX_LENGTH = 128;
export const EMAIL_MAX_LENGTH = 254;
export const NAME_MAX_LENGTH = 120;
export const INSTRUCTIONS_MAX_LENGTH = 4000;

/** Turnstile `action` names the API verifies (`{realm}_login`, `register`). */
export const TURNSTILE_ACTIONS = {
  consoleLogin: "console_login",
  adminLogin: "admin_login",
  register: "register",
} as const;
